import { inject, Injectable, Signal } from '@angular/core';
import { UrlUtils } from '../../shared/utils/url.utils';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JwtUtilService } from '../../core/jwt-util.service';
import { I18nService } from '../../core/i18n.service';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import {
    ClaimAsset,
    ClaimOutputList,
} from '../../shared/interfaces/claim-output-list.interface';
import {
    BehaviorSubject,
    map,
    Observable,
    retry,
    Subject,
    switchMap,
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../notifications/notifications.service';

@Injectable({
    providedIn: 'root',
})
export class ClaimOutputsService {
    private http = inject(HttpClient);
    private jwtUtilService = inject(JwtUtilService);
    private i18nService = inject(I18nService);
    private notifySvc = inject(NotificationsService);

    private notificationCountSubject = new BehaviorSubject<number>(0);
    notificationCount: Observable<number> =
        this.notificationCountSubject.asObservable();

    private dataRefreshSubject = new Subject<void>();

    institutionCode = UrlUtils.getParam('institution');

    notificationSignal: Signal<ClaimAsset[]>;

    constructor() {
        const notifySignal = toSignal(
            this.dataRefreshSubject.pipe(
                switchMap(() => this.getResearcherClaimOutputsList()),
                map((list: ClaimOutputList) =>
                    list.assets.map(
                        (output) =>
                            ({
                                ...output,
                                dateObject: new Date(
                                    output.createdYear,
                                    output.createdMonth,
                                    output.createdDay,
                                    output.createdHour,
                                    output.createdMinute,
                                    output.createdSecond,
                                ),
                            }) as ClaimAsset,
                    ),
                ),
                retry(),
            ),
            { initialValue: [] },
        );
        this.notifySvc.addStream('outputClaim', notifySignal);
        this.notificationSignal = notifySignal;
    }

    updateNotificationCount(total: number) {
        this.notificationCountSubject.next(total);
    }

    updateData() {
        this.dataRefreshSubject.next();
    }

    getResearcherClaimOutputsList(
        offset = 0,
        limit = null,
        taskIdsToExtract = [],
        sort = 'create_date',
    ) {
        let direction = 'desc';
        if (sort === 'asset_title') {
            direction = 'asc';
        }
        let params = new HttpParams()
            .set('institution', this.institutionCode)
            .set('offset', offset)
            .set('limit', limit || 1000)
            .set('sort', sort)
            .set('direction', direction)
            .set('language', this.i18nService.getLanguage())
            .set('userId', this.jwtUtilService.getUserID());
        for (const id of taskIdsToExtract) {
            params = params.append('taskIdsToExtract', id);
        }
        return this.http.get<ClaimOutputList>(
            RestApiBase.GET_RESEARCHER_APPROVAL_LIST,
            {
                params,
            },
        );
    }

    claim(taskIds) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this.http.put(
            RestApiBase.RESEARCHER_APPROVAL_CLAIM_ACTION,
            taskIds,
            { params },
        );
    }

    reject(taskIds) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this.http.put(
            RestApiBase.RESEARCHER_APPROVAL_REJECT_ACTION,
            taskIds,
            { params },
        );
    }
}
